import {makeShopifyRequest} from "../../request";
import type {CartLinesRemoveMutation} from "../../codegen/storefront.generated";
import {log} from "../../log";
import {isRegistrationOpen} from "../../wasm-shared/registration-open";
import type {CartDetails, CartLine} from "../storefront";

const MODULE = "packages/shopify/src/operations/storefront/removeCartLines.ts";

/**
 * Make sure the API call returned no user errors.
 */
function assertNoUserErrors(
	userErrors: NonNullable<
		NonNullable<CartLinesRemoveMutation["cartLinesRemove"]>["userErrors"]
	>
): void {
	if (!userErrors.length) {
		return;
	}

	log.error(
		{
			module: MODULE,
			fn: "removeCartLines",
			label: "RemoveCartItems",
			errors: userErrors,
		},
		"E_REMOVING_CART_ITEMS"
	);

	throw new Error(
		`Failed to remove cart lines: ${userErrors.map((e) => e.message).join("; ")}`
	);
}

/**
 * Make the actual API call to remove the lines from the cart.
 */
async function requestCartLinesRemove(
	cartId: string,
	lineIds: string[]
): Promise<CartLinesRemoveMutation["cartLinesRemove"]> {
	const {cartLinesRemove} = await makeShopifyRequest<CartLinesRemoveMutation>(
		"storefront",
		`#graphql
			mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
				cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
					cart {
						id
						checkoutUrl
						createdAt
						updatedAt
						lines(first: 100) {
							edges {
								node {
									id
									quantity
									attributes {
										key
										value
									}
									merchandise {
										__typename
										... on ProductVariant {
											id
											title
											price {
												amount
												currencyCode
											}
											isRegistrationOpenMetafield: metafield(
												namespace: "custom"
												key: "is_registration_open"
											) {
												value
											}
											product {
												title
											}
										}
									}
								}
							}
						}
					}
					userErrors {
						message
					}
				}
			}
		`,
		{cartId, lineIds}
	);

	return cartLinesRemove;
}

type CartLinesRemovePayload = NonNullable<
	CartLinesRemoveMutation["cartLinesRemove"]
>;
type RemovedCart = NonNullable<CartLinesRemovePayload["cart"]>;

/**
 * Make sure the API call returned a cart.
 */
function requireCart(
	cartLinesRemove: CartLinesRemoveMutation["cartLinesRemove"]
): RemovedCart {
	const cart = cartLinesRemove?.cart;
	if (!cart) {
		log.error(
			{
				module: MODULE,
				fn: "removeCartLines",
				label: "MissingCart",
				response: cartLinesRemove,
			},
			"E_MISSING_CART"
		);

		throw new Error("Shopify returned no cart");
	}

	return cart;
}

/**
 * Map the cart lines to the CartLine type.
 */
function mapCartLines(cart: RemovedCart): CartLine[] {
	return cart.lines.edges.flatMap(({node}) => {
		const merch = node.merchandise;
		if (merch.__typename !== "ProductVariant") return [];

		return [
			{
				id: node.id,
				quantity: node.quantity,
				variantId: merch.id,
				variantTitle: merch.title,
				productTitle: merch.product.title,
				priceAmount: merch.price.amount,
				priceCurrencyCode: merch.price.currencyCode,
				course_id: "",
				course_offering_id: "",
				campus_id: "",
				isRegistrationOpen: isRegistrationOpen(
					merch.isRegistrationOpenMetafield?.value
				),
				attributes: node.attributes.flatMap(({key, value}) =>
					value != null ? [{key, value}] : []
				),
			},
		];
	});
}

/**
 * @throws on unknown errors or missing cart
 */
export async function removeCartLines(
	cartId: string,
	lineIds: string[]
): Promise<CartDetails> {
	// Make the actual API call to remove the lines from the cart
	const cartLinesRemove = await requestCartLinesRemove(cartId, lineIds);

	// Make sure the API call was successful
	assertNoUserErrors(cartLinesRemove?.userErrors ?? []);

	// Make sure the API call returned a cart
	const cart = requireCart(cartLinesRemove);

	// Map the cart lines to the CartLine type
	const response = {
		id: cart.id,
		checkoutUrl: cart.checkoutUrl,
		createdAt: cart.createdAt,
		updatedAt: cart.updatedAt,
		lines: mapCartLines(cart),
	};

	return response;
}
